import React from "react";
import { Star, Quote } from "lucide-react";
import { Card, CardContent } from "./ui/card";

export const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Sarah Makigawan",
      role: "CEO, TechFlow Solutions",
      company: "Mid-size Tech Company",
      content:
        "We had a lot of manual work and a system that kept slowing us down. Creativorare helped us clean up the workflow, rebuild the key parts, and add automation where it mattered. The biggest difference is speed, we’re processing requests faster and our team isn’t stuck chasing the same issues every week. They communicated clearly, gave us realistic timelines, and delivered exactly what they promised.",
      rating: 5,
    },
    {
      name: "David Delacruz",
      role: "Founder, SmartRetail",
      company: "E-commerce Startup",
      content:
        "We came in with a rough idea and some sample screens, and Creativorare turned it into a working app that feels polished. They were quick to suggest better UX, and they didn’t disappear after launch when we needed fixes and improvements. The app is stable, fast, and our customers actually enjoy using it. If you’re an SME building your first mobile product, they’re a solid team to work with.",
      rating: 5,
    },
    {
      name: "Maria Rodriguez",
      role: "Operations Director, LogiChain",
      company: "Logistics Company",
      content:
        "We needed better visibility across our operations, but we didn’t want a complicated system that our staff would ignore. Creativorare designed a simple setup, integrated the data, and walked us through implementation step-by-step. We’re now catching issues earlier and spending less time on manual checks. What I appreciated most was how practical their approach was, no fluff, just solutions that work.",
      rating: 5,
    },
  ];

  return (
    <section
      id="testimonials"
      className="py-16 sm:py-20 md:py-24 bg-white dark:bg-gray-900 relative z-[1]"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
            Trusted by Growing Businesses
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-4">
            See what our clients say about working with us
          </p>
        </div>

        {/* Testimonials Grid - Responsive */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {testimonials.map((testimonial, index) => (
            <Card
              key={index}
              className="border-2 border-gray-100 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-500 dark:bg-gray-800 transition-all duration-300 hover:shadow-xl"
            >
              <CardContent className="p-4 sm:p-6 pt-4 sm:pt-6">
                <Quote className="w-8 h-8 sm:w-10 sm:h-10 text-purple-200 mb-3 sm:mb-4" />

                {/* Rating */}
                <div className="flex mb-3 sm:mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 sm:w-5 sm:h-5 fill-yellow-400 text-yellow-400"
                    />
                  ))}
                </div>

                {/* Content */}
                <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 mb-4 sm:mb-6 leading-relaxed">
                  "{testimonial.content}"
                </p>

                {/* Author */}
                <div className="border-t border-gray-100 dark:border-gray-700 pt-3 sm:pt-4">
                  <div className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white">
                    {testimonial.name}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    {testimonial.role}
                  </div>
                  <div className="text-xs sm:text-sm text-purple-600 dark:text-purple-400">
                    {testimonial.company}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Trust Badge - Responsive */}
        <div className="mt-10 sm:mt-12 md:mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4 md:space-x-8 bg-gradient-to-r from-purple-50 to-orange-50 dark:from-gray-800 dark:to-gray-800 px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6 rounded-xl sm:rounded-2xl">
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold text-purple-600 dark:text-purple-400">
                4.9/5
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Average Rating
              </div>
            </div>
            <div className="hidden sm:block h-10 md:h-12 w-px bg-gray-300 dark:bg-gray-600"></div>
            <div className="sm:hidden w-24 h-px bg-gray-300 dark:bg-gray-600"></div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold text-orange-500 dark:text-orange-400">
                50+
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Happy Clients
              </div>
            </div>
            <div className="hidden sm:block h-10 md:h-12 w-px bg-gray-300 dark:bg-gray-600"></div>
            <div className="sm:hidden w-24 h-px bg-gray-300 dark:bg-gray-600"></div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold text-yellow-600 dark:text-yellow-400">
                100%
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                On-Production Delivery
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
